import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { UserProfile, UserRole } from "../types/store";

const STORAGE_KEY = "modelerman.store.auth";

interface AuthContextValue {
    user: UserProfile | null;
    isLoading: boolean;
    login: (payload: { email: string; name?: string }) => Promise<void>;
    register: (payload: { email: string; password: string; name?: string }) => Promise<void>;
    logout: () => void;
    ensureRole: (role: UserRole) => void;
    updateProfile: (updates: Partial<UserProfile>) => void;
}

const StoreAuthContext = createContext<AuthContextValue | undefined>(undefined);

type ProviderProps = {
    children: React.ReactNode;
};

export const StoreAuthProvider: React.FC<ProviderProps> = ({ children }: ProviderProps) => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const cached = window.localStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached) as UserProfile;
                setUser(parsed);
            } catch (error) {
                console.warn("Failed to parse stored auth payload", error);
                window.localStorage.removeItem(STORAGE_KEY);
            }
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!user) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    }, [user]);

    const simulateNetwork = () => new Promise((resolve) => setTimeout(resolve, 600));

    const login = async ({ email, name }: { email: string; name?: string }) => {
        setIsLoading(true);
        await simulateNetwork();
        setUser((prev: UserProfile | null) => ({
            id: prev?.id ?? crypto.randomUUID(),
            email,
            name: name ?? prev?.name ?? "Modelerman Member",
            roles: prev?.roles ?? ["STORE_BUYER"]
        }));
        setIsLoading(false);
    };

    const register = async ({
        email,
        name
    }: {
        email: string;
        password: string;
        name?: string;
    }) => {
        setIsLoading(true);
        await simulateNetwork();
        setUser({
            id: crypto.randomUUID(),
            email,
            name: name ?? "Modelerman Creator",
            roles: ["STORE_BUYER"]
        });
        setIsLoading(false);
    };

    const logout = () => setUser(null);

    const ensureRole = (role: UserRole) => {
        setUser((current: UserProfile | null) => {
            if (!current) return current;
            if (current.roles.includes(role)) return current;
            return { ...current, roles: [...current.roles, role] };
        });
    };

    const updateProfile = (updates: Partial<UserProfile>) => {
        setUser((current: UserProfile | null) => (current ? { ...current, ...updates } : current));
    };

    const value = useMemo<AuthContextValue>(
        () => ({ user, isLoading, login, register, logout, ensureRole, updateProfile }),
        [user, isLoading]
    );

    return <StoreAuthContext.Provider value={value}>{children}</StoreAuthContext.Provider>;
};

export const useStoreAuth = () => {
    const ctx = useContext(StoreAuthContext);
    if (!ctx) {
        throw new Error("useStoreAuth must be used within a StoreAuthProvider");
    }
    return ctx;
};
